import styled from "@emotion/styled";
import { useMemo, useState, type RefObject } from "react";
import { createPortal } from "react-dom";

import { asPixels, DropIndicator, zIndexes } from "@skilitics/designer-ui-components";
import type { Collision } from "@skilitics/drag-drop";
import { useDragDropMonitor, useDragOperation } from "@skilitics/drag-drop";

import { DND_CANVAS_EDITOR_ID, draggableDataSchema, getInsertPosition } from "../dnd";

const DropInsideBorder = styled.div`
  position: absolute;
  outline: solid 3px ${({ theme }) => theme.secondary};
  pointer-events: none;
  z-index: ${zIndexes.composableNodeDropIndicator};
`;

type Offset = {
  top: number;
  left: number;
};

const getBorderStyles = (domRef: RefObject<HTMLElement | null>, offset: Offset) => {
  if (!domRef.current) return {};

  const { top, left, height, width } = domRef.current.getBoundingClientRect();

  return {
    top: asPixels(top - offset.top),
    left: asPixels(left - offset.left),
    width: asPixels(width),
    height: asPixels(height),
  };
};

type Props = {
  domRef: RefObject<HTMLElement | null>;
  isRootContainer?: boolean;
  /**
   * Hide before/after indicators, as outside the flow of parent element.
   */
  isAbsolutelyPositioned?: boolean;
};

export const CanvasDropIndicator = ({ domRef, isRootContainer, isAbsolutelyPositioned }: Props) => {
  const editorContainer = document.getElementById(DND_CANVAS_EDITOR_ID);
  const { source } = useDragOperation();
  const [activeCollisions, setActiveCollisions] = useState<Collision[]>([]);
  const collisionHandlers = useMemo(
    () => ({
      onCollision: (event: { collisions: Collision[] }) => setActiveCollisions(event.collisions),
      onDragEnd: () => setActiveCollisions([]),
    }),
    [],
  );
  useDragDropMonitor(collisionHandlers);
  const insertPosition = useMemo(() => getInsertPosition(activeCollisions, source?.id), [activeCollisions, source?.id]);

  // Absolutely positioned elements don't need a drop indicator - they reposition within their parent
  const draggableData = draggableDataSchema.safeParse(source?.data);
  if (draggableData.success && draggableData.data.isAbsolutelyPositioned && !draggableData.data.inLayersPanel) {
    return null;
  }

  const editorContainerOffset: Offset = {
    top: editorContainer?.getBoundingClientRect()?.top ?? 0,
    left: editorContainer?.getBoundingClientRect()?.left ?? 0,
  };

  return (
    !!editorContainer &&
    !!insertPosition &&
    !(isAbsolutelyPositioned && insertPosition !== "inside") &&
    createPortal(
      isRootContainer || insertPosition === "inside" ? (
        <DropInsideBorder style={getBorderStyles(domRef, editorContainerOffset)} />
      ) : (
        <DropIndicator insertPosition={insertPosition} domRef={domRef} offset={editorContainerOffset} />
      ),
      editorContainer,
    )
  );
};
